/**
 * PaystubsTile — bulletins de l'année : liste, détail, PDF, chèque, envoi courriel (Batch #14.250).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText, Banknote, Mail, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, fmtCAD, downloadFile, StatusBadge, PAYMENT_METHODS, thCls, tdCls, err, inputCls } from "./_shared";
import StubBreakdown from "./StubBreakdown";

export default function PaystubsTile({ masterId, year }) {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [empFilter, setEmpFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([
        axios.get(`${API}/admin/payroll/${masterId}/paystubs?year=${year}${empFilter ? `&employee_id=${empFilter}` : ""}`, { headers: authH() }),
        axios.get(`${API}/admin/payroll/${masterId}/employees`, { headers: authH() }),
      ]);
      setItems(r.data.items || []); setEmps(e.data.items || []);
    } catch (x) { err(x); }
  }, [masterId, year, empFilter]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    if (open === s.id) { setOpen(null); return; }
    setOpen(s.id);
    if (!detail[s.id]) {
      try { const r = await axios.get(`${API}/admin/payroll/${masterId}/paystubs/${s.id}`, { headers: authH() }); setDetail((d) => ({ ...d, [s.id]: r.data })); } catch (x) { err(x); }
    }
  };
  const sendOne = async (s) => {
    if (!window.confirm(`Envoyer ce bulletin par courriel à ${s.employee.display_name} ?`)) return;
    setBusy(`send-${s.id}`);
    try {
      const r = await axios.post(`${API}/admin/payroll/${masterId}/paystubs/${s.id}/send`, {}, { headers: authH() });
      if (r.data.sent) toast.success("Bulletin envoyé"); else toast.error(r.data.entries?.[0]?.info === "aucun courriel" ? "Aucun courriel dans la fiche employé" : `Échec : ${r.data.entries?.[0]?.info || ""}`);
    } catch (x) { err(x); } finally { setBusy(""); }
  };
  const totals = items.reduce((a, s) => ({ gross: a.gross + s.result.gross, net: a.net + s.result.net }), { gross: 0, net: 0 });

  return (
    <div className="space-y-4" data-testid="paie-paystubs">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <select className={`${inputCls} sm:w-72`} value={empFilter} onChange={(e) => setEmpFilter(e.target.value)} data-testid="paie-stubs-emp-filter">
          <option value="">Tous les employés</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.display_name}</option>)}
        </select>
        <div className="text-xs text-slate-600 font-mono">{items.length} bulletin(s) · brut {fmtCAD(totals.gross)} · net {fmtCAD(totals.net)}</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {items.length === 0 ? <div className="p-10 text-center text-sm text-slate-500" data-testid="paie-stubs-empty">Aucun bulletin en {year}. Calculez une période de paie pour en générer.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className={thCls}></th><th className={thCls}>Paie le</th><th className={thCls}>Employé</th><th className={thCls}>Période</th>
                <th className={`${thCls} text-right`}>Brut</th><th className={`${thCls} text-right`}>Retenues</th><th className={`${thCls} text-right`}>Net</th><th className={thCls}>Paiement</th><th className={thCls}>Statut</th><th className={`${thCls} text-right`}>Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((s) => (
                  <StubRow key={s.id} s={s} isOpen={open === s.id} detail={detail[s.id]} busy={busy} masterId={masterId} onToggle={() => toggle(s)} onSend={() => sendOne(s)} setBusy={setBusy} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StubRow({ s, isOpen, detail, busy, masterId, onToggle, onSend, setBusy }) {
  const approved = ["approved", "paid"].includes(s.status);
  const dl = (path, name, key) => downloadFile(`/admin/payroll/${masterId}/paystubs/${s.id}/${path}`, name, (b) => setBusy(b ? key : ""));
  return (
    <>
      <tr className="hover:bg-slate-50/60" data-testid={`paie-stub-row-${s.id}`}>
        <td className="px-2"><button onClick={onToggle} className="p-1 rounded hover:bg-slate-100" aria-label="Détail" data-testid={`paie-stub-toggle-${s.id}`}>{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button></td>
        <td className={`${tdCls} whitespace-nowrap`}>{s.pay_date}</td>
        <td className={`${tdCls} font-semibold text-slate-800`}>{s.employee.display_name}<div className="text-[11px] text-slate-400 font-normal">{s.employee.employee_no}</div></td>
        <td className={`${tdCls} whitespace-nowrap text-xs`}><Link to={`/admin/paie/${masterId}/periodes/${s.run_id}`} className="hover:underline">{s.period_start} → {s.period_end}</Link></td>
        <td className={`${tdCls} text-right font-mono`}>{fmtCAD(s.result.gross)}</td>
        <td className={`${tdCls} text-right font-mono`}>{fmtCAD(s.result.deductions.total)}</td>
        <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCAD(s.result.net)}</td>
        <td className={`${tdCls} text-xs`}>{PAYMENT_METHODS[s.employee.payment_method]}{s.cheque_no ? ` n° ${s.cheque_no}` : ""}</td>
        <td className={tdCls}><StatusBadge status={s.status} /></td>
        <td className={`${tdCls} text-right whitespace-nowrap`}>
          <button onClick={() => dl("pdf", `bulletin_${s.pay_date}_${s.employee.employee_no || ""}.pdf`, `pdf-${s.id}`)} disabled={busy === `pdf-${s.id}`} className="p-1.5 rounded-lg text-indigo-700 hover:bg-indigo-50" title="Bulletin PDF" data-testid={`paie-stub-pdf-${s.id}`}><FileText className="w-4 h-4" /></button>
          <button onClick={() => dl("cheque.pdf", `cheque_${s.pay_date}.pdf`, `chq-${s.id}`)} disabled={!approved || busy === `chq-${s.id}`} className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-50 disabled:opacity-30" title={approved ? "Chèque PDF" : "Approuvez la période d'abord"} data-testid={`paie-stub-cheque-${s.id}`}><Banknote className="w-4 h-4" /></button>
          <button onClick={onSend} disabled={!approved || busy === `send-${s.id}`} className="p-1.5 rounded-lg text-sky-700 hover:bg-sky-50 disabled:opacity-30" title={approved ? "Envoyer par courriel" : "Approuvez la période d'abord"} data-testid={`paie-stub-send-${s.id}`}><Mail className="w-4 h-4" /></button>
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-white"><td colSpan={10} className="px-4 py-3">
          {detail ? <StubBreakdown result={detail.result} ytd={detail.ytd_before} ytdLines={detail.ytd_lines} employerLines={detail.employer_lines} /> : <p className="text-xs text-slate-500">Chargement…</p>}
        </td></tr>
      )}
    </>
  );
}
